/**
 * Reusable content-blocks picker — shared between the inline `/` slash menu and the
 * "Add content" button. Renders the slash-style 2-column grid in a PopoverSurface.
 *
 * Keyboard nav: ↑↓ jump rows, ←→ jump columns, Enter/Tab commits, Escape closes.
 * `query` opts the picker into typeahead filtering (label + hint substring match).
 */

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { observer } from 'mobx-react-lite'
import { makeAutoObservable } from 'mobx'
import { themeConfig } from '@gap/shared/theme'
import useStore from 'reusable/mobx-store-utils'
import DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import PopoverSurface, { PopoverPosition } from './PopoverSurface'
import { GroupHeader } from './GroupHeader'
import { ContentBlockMenuItem, ContentBlockType, contentBlocks } from '../contentBlocks'

interface ContentBlocksMenuProps {
   anchor: PopoverPosition
   /** Optional typeahead filter. When omitted, all blocks are shown. */
   query?: string
   onSelect: (type: ContentBlockType) => void
   onClose: () => void
}

class ContentBlocksMenuStore {
   sp: ContentBlocksMenuProps
   active = -1

   constructor(sp: ContentBlocksMenuProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get items(): ContentBlockMenuItem[] {
      const query = this.sp.query?.trim().toLowerCase() ?? ''
      if (!query) return contentBlocks
      return contentBlocks.filter(it => it.label.toLowerCase().includes(query) || it.hint.toLowerCase().includes(query))
   }

   configureReactions = (reactionsStore: DisposableReactionsStore) => {
      window.addEventListener('keydown', this.handleKeyDown, true)
      reactionsStore.registerDisposer(() => window.removeEventListener('keydown', this.handleKeyDown, true))
      reactionsStore.registerReaction({
         observedExpression: () => this.items.length,
         reaction: () => { this.active = -1 },
      })
   }

   commit = (item: ContentBlockMenuItem) => {
      this.sp.onSelect(item.type)
   }

   private handleKeyDown = (event: KeyboardEvent) => {
      const count = this.items.length
      if (!count) {
         if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
            this.sp.onClose()
         }
         return
      }
      if (event.key === 'ArrowDown') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.active = this.active === -1 ? 0 : Math.min(count - 1, this.active + 2)
      } else if (event.key === 'ArrowUp') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.active = this.active === -1 ? 0 : Math.max(0, this.active - 2)
      } else if (event.key === 'ArrowRight') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.active = this.active === -1 ? 0 : Math.min(count - 1, this.active + 1)
      } else if (event.key === 'ArrowLeft') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.active = this.active === -1 ? 0 : Math.max(0, this.active - 1)
      } else if (event.key === 'Enter' || event.key === 'Tab') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         const item = this.items[this.active]
         if (item) this.commit(item)
      } else if (event.key === 'Escape') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.sp.onClose()
      }
   }
}

interface ContentBlockRowProps {
   item: ContentBlockMenuItem
   selected: boolean
   onSelect: (item: ContentBlockMenuItem) => void
}

const ContentBlockRow = observer(function ContentBlockRow(props: ContentBlockRowProps) {
   const rowStore = useStore(sp => ({
      handleMouseDown: (event: React.MouseEvent) => {
         event.preventDefault()
         sp.onSelect(sp.item)
      },
   }), props)

   return <Button
      onMouseDown={rowStore.handleMouseDown}
      sx={{
         justifyContent: 'flex-start',
         textTransform: 'none',
         padding: '10px',
         borderRadius: '4px',
         color: themeConfig.primary[800],
         bgcolor: props.selected ? 'action.focus' : 'transparent',
         '&:hover': { bgcolor: 'action.hover' },
         fontSize: 14,
         fontWeight: 500,
         gap: '10px',
      }}
   >
      <props.item.icon sx={{ fontSize: 20, color: themeConfig.primary[800] }} />
      <Box component="span">{props.item.label}</Box>
   </Button>
})

const ContentBlocksMenu = observer(function ContentBlocksMenu(props: ContentBlocksMenuProps) {
   const store = useStore(sp => new ContentBlocksMenuStore(sp), props)

   return <PopoverSurface anchor={props.anchor} width={360} maxHeight={420} onDismiss={props.onClose}>
      <Box sx={{ padding: '8px 16px 4px' }}>
         <GroupHeader>Content blocks</GroupHeader>
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1, padding: '4px 8px 8px' }}>
         {store.items.length === 0 ? (
            <Box sx={{ padding: '20px 16px', color: themeConfig.primary[500], fontSize: 13, textAlign: 'center' }}>
               <Box sx={{ marginBottom: '4px', color: themeConfig.primary[800], fontWeight: 500 }}>No matches</Box>
               <Box>Try a different word.</Box>
            </Box>
         ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
               {store.items.map((item, index) => (
                  <ContentBlockRow
                     key={item.type}
                     item={item}
                     selected={index === store.active}
                     onSelect={store.commit}
                  />
               ))}
            </Box>
         )}
      </Box>
   </PopoverSurface>
})

export default ContentBlocksMenu
